<template>
  <div class="cl-list" :class="{ 'cl-loading': loading }">
    <!-- Barre d'outils : recherche, nombre de résultats, vue, export -->
    <div class="catalogue-toolbar cl-toolbar">
      <div class="cl-toolbar-left">
        <div class="catalogue-search-wrap">
          <span class="catalogue-search-icon">
            <svg class="handoff-icon" width="15" height="15" aria-hidden="true"><use :href="sprite + '#i-search'"></use></svg>
          </span>
          <input v-model="search" class="catalogue-search" type="search" placeholder="Code, nom, email" aria-label="Rechercher un bon cadeau" />
        </div>
        <select v-model.number="perPage" class="catalogue-select" aria-label="Bons par page">
          <option :value="12">12 par page</option>
          <option :value="24">24 par page</option>
          <option :value="48">48 par page</option>
        </select>
        <span class="cl-count">{{ countLabel }}</span>
      </div>
      <div class="cl-toolbar-right">
        <div class="cl-view-switch" role="group" aria-label="Affichage">
          <button type="button" :class="{ 'is-active': viewMode === 'card' }" @click="viewMode = 'card'">Cartes</button>
          <button type="button" :class="{ 'is-active': viewMode === 'table' }" @click="viewMode = 'table'">Tableau</button>
        </div>
        <div class="catalogue-export">
          <span class="catalogue-export-label">Exporter</span>
          <button type="button" class="catalogue-export-button" @click="exportCsv">CSV</button>
          <button type="button" class="catalogue-export-button" @click="exportXls">Excel</button>
        </div>
      </div>
    </div>

    <!-- Cartes -->
    <div v-if="viewMode === 'card'" class="catalogue-grid">
      <article v-for="item in items" :key="item.id" class="catalogue-card po-card vc-card">
        <div class="po-card-main">
          <div class="po-card-title">
            <h2 class="catalogue-name">Bon {{ item.code }}</h2>
            <span v-html="item.status"></span>
          </div>
          <div class="vc-people">
            <span><small>De</small> {{ person(item, 'from') }}</span>
            <span><small>Pour</small> {{ person(item, 'to') }}</span>
          </div>
          <div class="catalogue-meta">
            <div>
              <span class="catalogue-meta-label">Montant</span>
              <span class="catalogue-meta-value">{{ text(item.total_amount) || '-' }}</span>
            </div>
            <div>
              <span class="catalogue-meta-label">Restant</span>
              <span class="catalogue-meta-value">{{ text(item.available_amount) || '-' }}</span>
            </div>
            <div>
              <span class="catalogue-meta-label">Expire le</span>
              <span class="catalogue-meta-value">{{ (item.date_end && item.date_end.display) || '-' }}</span>
            </div>
          </div>
        </div>
        <div class="catalogue-card-actions" v-html="item.actions"></div>
      </article>
      <div v-if="!loading && !items.length" class="catalogue-empty">{{ search ? 'Aucun bon ne correspond.' : 'Aucun bon cadeau ici.' }}</div>
    </div>

    <!-- Tableau : colonnes choisies par l'utilisateur -->
    <div v-else class="cl-table-wrap">
      <table class="cl-table">
        <thead>
          <tr>
            <th v-for="col in tableColumns" :key="col.data">{{ col.data === 'actions' ? '' : col.title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td v-for="col in tableColumns" :key="col.data">
              <span v-if="col.data === 'send_date' || col.data === 'date_end'">{{ item[col.data] && item[col.data].display }}</span>
              <span v-else v-html="item[col.data]"></span>
            </td>
          </tr>
          <tr v-if="!loading && !items.length">
            <td :colspan="tableColumns.length" class="text-center po-meta-help">{{ search ? 'Aucun bon ne correspond.' : 'Aucun bon cadeau ici.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="cl-pagination" v-if="totalPages > 1">
      <span class="cl-count">Page {{ page }} sur {{ totalPages.toLocaleString('fr-FR') }}</span>
      <nav class="cl-pages" aria-label="Pagination">
        <button type="button" :disabled="page === 1 || loading" @click="page--" aria-label="Page précédente">
          <svg class="handoff-icon" width="14" height="14" aria-hidden="true"><use :href="sprite + '#i-chevron-left'"></use></svg>
        </button>
        <template v-for="(p, index) in visiblePages">
          <span v-if="index && p - visiblePages[index - 1] > 1" :key="'gap-' + p">…</span>
          <button type="button" :key="p" :class="{ 'is-active': p === page }" :aria-current="p === page ? 'page' : null" @click="page = p">{{ p }}</button>
        </template>
        <button type="button" :disabled="page === totalPages || loading" @click="page++" aria-label="Page suivante">
          <svg class="handoff-icon" width="14" height="14" aria-hidden="true"><use :href="sprite + '#i-chevron-right'"></use></svg>
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    fetchUrl: String,
    exportCsvUrl: String,
    exportXlsUrl: String,
    columns: Array
  },

  data () {
    return {
      items: [],
      page: 1,
      perPage: 24,
      totalAll: 0,
      totalFiltered: null,
      search: '',
      loading: false,
      searchTimeout: null,
      viewMode: 'card',
      sprite: '/media/svg/handoff-icons/icons-sprite.svg'
    }
  },

  watch: {
    page () {
      this.fetchData()
    },

    perPage () {
      this.page = 1
      this.fetchData()
    },

    search () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.page = 1
        this.fetchData()
      }, 300)
    },

    viewMode (val) {
      try { localStorage.setItem('voucherViewMode', val) } catch (e) {}
    }
  },

  mounted () {
    try { this.viewMode = localStorage.getItem('voucherViewMode') || 'card' } catch (e) {}
    this.fetchData()
  },

  methods: {
    async fetchData () {
      this.loading = true

      try {
        const params = {
          draw: Date.now(),
          start: (this.page - 1) * this.perPage,
          length: this.perPage,
          'search[value]': this.search || '',
          'search[regex]': false
        }

        this.columns.forEach((col, index) => {
          params[`columns[${index}][data]`] = col.data
          params[`columns[${index}][name]`] = col.name || col.data
          params[`columns[${index}][searchable]`] = true
          params[`columns[${index}][orderable]`] = true
        })

        const response = await axios.get(this.fetchUrl, { params })
        this.items = response.data.data ?? []
        this.totalAll = Number(response.data.recordsTotal ?? 0)
        this.totalFiltered = Number(response.data.recordsFiltered ?? this.totalAll)
      } catch (e) {
        console.error('Erreur chargement', e)
      } finally {
        this.loading = false
      }
    },

    // Colonnes mises en forme côté serveur (HTML) : texte seul pour les cartes
    text (value) {
      if (value === null || value === undefined) return ''
      const element = document.createElement('div')
      element.innerHTML = String(value)
      return element.textContent.replace(/\s+/g, ' ').trim()
    },

    // Acheteur (from) ou bénéficiaire (to) : fiche liée, sinon les champs saisis sur le bon
    person (item, side) {
      const user = item[side]
      const name = user
        ? [user.first_name, user.last_name]
        : [item[side + '_first_name'], item[side + '_last_name']]
      return name.filter(Boolean).join(' ') || '-'
    },

    exportCsv () {
      window.location.href = this.exportCsvUrl
    },

    exportXls () {
      window.location.href = this.exportXlsUrl
    }
  },

  computed: {
    // Les actions restent disponibles même si la colonne n'a pas été choisie
    tableColumns () {
      return this.columns.some((col) => col.data === 'actions')
        ? this.columns
        : [...this.columns, { data: 'actions', title: '' }]
    },

    total () {
      return this.totalFiltered ?? this.totalAll
    },

    countLabel () {
      if (this.loading && !this.items.length) return 'Chargement…'
      const total = this.total.toLocaleString('fr-FR')
      return `${total} bon${this.total > 1 ? 's' : ''}`
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    },

    // Première, dernière et pages voisines de la page courante
    visiblePages () {
      const pages = new Set([1, this.totalPages])
      for (let i = this.page - 1; i <= this.page + 1; i++) {
        if (i >= 1 && i <= this.totalPages) pages.add(i)
      }
      return [...pages].sort((a, b) => a - b)
    }
  }
}
</script>
